/** Phone numbers and search text: helpers for display, typing, and matching. */

/** Digits only, without a leading US country code. */
export function phoneDigits(raw: string | null | undefined): string {
  const d = (raw ?? "").replace(/\D/g, "");
  return d.length === 11 && d.startsWith("1") ? d.slice(1) : d;
}

/** (405) 555-0123 for 10-digit US numbers; anything else as entered. */
export function formatPhone(raw: string | null | undefined): string {
  const d = phoneDigits(raw);
  if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  return (raw ?? "").trim();
}

/**
 * As someone types a phone number: "4055550" → "(405) 555-0", "+1 405.555.0123" → "(405) 555-0123".
 * Extensions, letters, other countries, and more than 10 digits are left as typed.
 */
export function typePhone(raw: string): string {
  if (/[A-Za-z]/.test(raw) || (/^\s*\+/.test(raw) && !/^\s*\+\s*1/.test(raw))) return raw;
  let d = raw.replace(/\D/g, "");
  // US area codes never start with 1, so a leading 1 is the country code.
  if (d === "1") return raw;
  if (d.startsWith("1")) d = d.slice(1);
  if (d.length > 10) return raw;
  if (!d) return "";
  if (d.length <= 3) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

/** Letters and digits only, lowercase: "O'Brien" → "obrien", "1412 S.W. 4th" → "1412sw4th". For searching. */
export function squash(s: string | null | undefined): string {
  return (s ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
}

/** Whether the text has the search in it, ignoring case, spaces, and punctuation. */
export function looselyIncludes(text: string | null | undefined, q: string): boolean {
  const t = (text ?? "").toLowerCase();
  const plain = q.trim().toLowerCase();
  if (!plain || t.includes(plain)) return true;
  const s = squash(q);
  return !!s && squash(text).includes(s);
}
